<template>
<div>
    <advanced-filter-btn :label="displayName" :active="hasAppliedFilter" data-test="students3d-filter-by-demographic-btn" @click="dialog=true" />
    <v-dialog v-model="dialog" width="600" :persistent="persistDialog">
        <fe-stepper-filter
            :title="title" style="height:600px;"
            @apply="emit" @close="dialog=false"
            :applyDisabled="!enableFinalPanel"
        >
            <fe-stepper-select-panel :items="demographicItems" v-model="demographic" width="50%" />
            <v-divider vertical style="background-color:#E0E1E6"/>

            <fe-stepper-panel width="50%" style="padding:10px;">
                <template v-if="enableFinalPanel">
                    <fe-remote-combo
                        label="School Year"
                        infoText="If a student was exited at any time during the year, they will be considered exited for the entire year."
                        v-model="panelValues.school_year"
                        rootProperty="years"
                        url="qry.php?action=get&property=school_years"
                    ></fe-remote-combo>
                    <v-layout row>
                        <v-flex class="pr-2">
                            <v-switch v-model="panelValues.restrict" label="Restrict Results" hide-details :ripple="false" />
                        </v-flex>
                        <v-flex v-if="allowsRequireAll">
                            <v-switch v-model="panelValues.require_all" label="Require All" hide-details :ripple="false" />
                        </v-flex>
                    </v-layout>

                    <advanced-demographic-filter
                        v-model="demographicParams"
                        :demographic="demographic"
                        @result="parseDemographicResult"
                    />

                </template>
            </fe-stepper-panel>

            <template #footer>
                <div class="text-truncate" style="padding:14px">{{breadcrumb}}</div>
            </template>
        </fe-stepper-filter>
    </v-dialog>
</div>
</template>

<script>
import * as Util from '../../Util'
import AdvancedDemographicFilter from "@/components/common/AdvancedDemographicFilter"
import FilterUtil from '../../FilterPanelUtil'
import AdvancedFilterBtn from './AdvancedFilterBtn'
import escapeCloseDialogMixin from './escapeCloseDialogMixin'

export default {
    name: 'DemographicsPanel',
    mixins: [escapeCloseDialogMixin],
    rank: 1,

    components: {
        AdvancedFilterBtn,
        AdvancedDemographicFilter,
    },
    inject: ['localStore'],

    data() {
        return {
            oldFilter: null,
            displayName: 'Demographic',
            type: 'demographic',
            dialog: false,
            panelValues: {
                restrict: true,
                require_all: false,
                school_year: null,
                demographicResult: '',
            },
            demographic: null,
            demographicParams: null,
            persistDialog: false,
            panelOpenState: {}
        }
    },

    watch: {
        dialog(v) {
            if (!v) this.oldFilter = null, this.clear()
            if (v) this.panelOpenState = this.$_.clone(this.panelValues)
        },
        allowsRequireAll(v) {
            if (!v) this.panelValues.require_all = false
        },
        computedPanelValues: {
            handler(newValue) {
                this.persistDialog = FilterUtil.hasFormStateChanged(newValue, this.panelOpenState)
            }
        },
    },

    computed: {
        demographicItems: me => me.$store.getters['global/demographicsViewable'],
        hasAppliedFilter() {
            const filters = this.localStore?.state?.params?.filters
            return !!filters?.some(f => f.type === this.type || (typeof f.type === 'string' && f.type.startsWith('demo_')))
        },
        title: me => me.oldFilter ? `Edit ${me.displayName} Filter` : `Create ${me.displayName} Filter`,
        allowsRequireAll: me => me.demographic?.value_type === 'option',
        enableFinalPanel: me => !me.$_.isEmpty(me.demographic),
        breadcrumb: me => me.demographic?.name,
        computedPanelValues() {
            return Object.assign({}, this.panelValues)
        }
    },

    created() {
        this.$store.dispatch('global/loadDemographics')
    },

    methods: {
        clear() {
            this.panelValues = {
                restrict : true,
                demographicResult : '',
                require_all: false,
                school_year: null
            }
            this.demographic = null
            this.demographicParams = null
        },

        load(oldFilter) {
            this.oldFilter = oldFilter
            this.dialog = true
            let ep = oldFilter.editorParams

            this.panelValues = {
                restrict : ep.restrict,
                demographicResult : ep.demographicResult,
                require_all : ep.require_all,
                school_year : ep.school_year
            }

            this.demographic = ep.demographic
            this.demographicParams = ep.demographicParams
        },

        emit() {
            this.dialog = false
            let uniqueId = Util.UUIDv4()
            let name = this.demographic.name
            let key = `demo_${this.demographic.id}`
            let filter = {
                type: key,
                id: uniqueId,
                restrict: this.panelValues.restrict,
                report: true,
                params: this.$_.pickBy({
                    [key]: this.demographicParams,
                    [uniqueId]: this.demographic.value_type === 'option' ? this.displayNamesWithIds() : null,
                    require_all: this.allowsRequireAll && this.panelValues.require_all,
                    school_year_id: this.panelValues.school_year?.id,
                }, x => !this.$_.isBlank(x)),
            }
            Object.defineProperties(filter, {
                editor: {value: this},
                editorParams: {value: {
                    restrict: this.panelValues.restrict,
                    demographic: this.demographic ? {...this.demographic} : null,
                    demographicParams: this.demographicParams,
                    demographicResult: this.panelValues.demographicResult ? {...this.panelValues.demographicResult} : null,
                    require_all: this.panelValues.require_all,
                    school_year: this.panelValues.school_year ? {...this.panelValues.school_year} : null,
                }},
                grouper: {value: {
                    id: uniqueId,
                    name: name,
                    type: 'propertyArray',
                    args: {path: `filters.${uniqueId}`, property: 'value'},
                }},
                chipText: {value: name},
                tooltipData: {value: this.$_.pickBy({
                    'Demographic': name,
                    'Params': this.panelValues.demographicResult?.textWithOp,
                    'Restrict': this.panelValues.restrict,
                    'Require All': this.allowsRequireAll ? this.panelValues.require_all : null,
                    'School Year': this.panelValues.school_year?.name,
                }, x => !this.$_.isBlank(x))}
            })
            this.$emit('input', filter, this.oldFilter)
            this.oldFilter = filter
        },

        parseDemographicResult(e) {
            this.panelValues.demographicResult = ''
            if(e) {
                this.$parseDemographic(e.key,e.encoded).then(r => {
                    this.panelValues.demographicResult = r
                })
            }
        },

        /**
         * Build id/value map for option-type demographics. Uses encoded ids in demographicParams
         * (comma-separated, same as $parseDemographic) — not demographicResult.text — so values like
         * "Garcia, Danny" are not broken by splitting on ", ".
         */
        displayNamesWithIds() {
            const options = this.demographic?.options
            if (!options?.length) {
                return {}
            }
            const encoded = this.demographicParams
            if (this.$_.isBlank(encoded)) {
                return {}
            }
            const parts = `${encoded}`.split(',').map(s => s.trim()).filter(Boolean)
            const byKey = new Map()

            parts.forEach((part) => {
                const idStr = part[0] === '!' ? part.slice(1) : part
                let option = options.find(o => String(o.id) === String(idStr))
                if (!option) {
                    const member = options.find(
                        o => o.display_name_group && o.display_name_group.some(g => String(g) === String(idStr))
                    )
                    if (member && member.display_name_group && member.display_name_group.length) {
                        option = options.find(o => String(o.id) === String(member.display_name_group[0])) || member
                    }
                }
                if (!option) {
                    return
                }
                const key = option.display_name_group
                    ? `g:${option.display_name_group[0]}`
                    : `i:${option.id}`
                if (byKey.has(key)) {
                    return
                }
                byKey.set(key, {
                    ids: option.display_name_group || option.id,
                    value: option.display_name || option.value
                })
            })

            const demo = {}
            let idx = 0
            byKey.forEach((val) => {
                demo[idx++] = val
            })
            return demo
        }
    }
}
</script>

<style lang="scss" scoped>
</style>
